if (!customElements.get('product-mobile-heading')) {
  customElements.define('product-mobile-heading', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section--main-product');
      this.content = this.section?.querySelector('[data-product-panel] > .sticky');
      if (!this.content) return;
      this.placements = new Map();
      this.mediaQuery = window.matchMedia('(max-width: 767px)');
      this.onBreakpointChange = () => this.update();
      this.mediaQuery.addEventListener('change', this.onBreakpointChange);
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this.content, { childList: true, subtree: true });
      this.observer.observe(this, { childList: true, subtree: true });
      this.update();
    }

    update() {
      if (!this.isConnected) return;
      for (const [block, anchor] of this.placements) {
        if (!block.isConnected) {
          anchor.remove();
          this.placements.delete(block);
        }
      }
      if (!this.mediaQuery.matches) {
        this.restore();
        return;
      }
      const title = this.section.querySelector('.product-block__title');
      const badge = this.section.querySelector('.jdgm-preview-badge')?.closest('.product-block');
      const blocks = [title, badge].filter(block => block && (this.content.contains(block) || this.contains(block)));
      blocks.forEach(block => {
        if (!this.placements.has(block)) {
          const anchor = document.createComment('');
          block.before(anchor);
          this.placements.set(block, anchor);
        }
      });
      const current = [...this.children].filter(block => blocks.includes(block));
      if (current.length !== blocks.length || current.some((block, index) => block !== blocks[index])) {
        blocks.forEach(block => this.append(block));
      }
    }

    restore() {
      for (const [block, anchor] of this.placements) {
        if (anchor.parentNode && this.contains(block)) anchor.replaceWith(block);
        else anchor.remove();
      }
      this.placements.clear();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.mediaQuery?.removeEventListener('change', this.onBreakpointChange);
      if (this.placements) this.restore();
    }
  });
}

class ProductBuyBar extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    this.form = document.getElementById(this.dataset.formId);
    this.button = this.querySelector('[data-sticky-submit]');
    this.main = this.form?.closest('product-form');
    if (!this.main || !this.button) return;
    this.abort = new AbortController();
    this.addEventListener('click', event => {
      if (!event.target.closest('[data-sticky-submit]')) return;
      event.preventDefault();
      event.stopPropagation();
      const buttons = this.purchaseButtons();
      if (buttons.length !== 1) {
        this.main.handleErrorMessage('Please review your product options and use the main Add to cart button.');
        this.main.setAttribute('tabindex', '-1');
        this.main.focus({ preventScroll: true });
        this.main.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      if (!this.blocked(buttons[0])) buttons[0].click();
    }, { capture: true, signal: this.abort.signal });
    this.mutations = new MutationObserver(() => this.sync());
    this.mutations.observe(this.main, { subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'class', 'style', 'hidden'] });
    this.observer = new IntersectionObserver(([entry]) => {
      this.classList.toggle('show', !entry.isIntersecting && window.scrollY > 200);
    }, { threshold: 0.25 });
    this.observer.observe(this.form);
    this.sync();
  }

  purchaseButtons() {
    return [...this.main.querySelectorAll('[name="add"], [dpo_add2cart], [data-ph-cart-clone]')].filter(button => {
      if (button.closest('.shopify-payment-button') || button.closest('product-form') !== this.main) return false;
      for (let node = button; node; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (node.hidden || style.display === 'none' || style.visibility === 'hidden') return false;
      }
      return true;
    });
  }

  blocked(button) {
    return button.disabled || button.getAttribute('aria-disabled') === 'true' ||
      button.getAttribute('aria-busy') === 'true' || button.classList.contains('submitting') || button.classList.contains('loading');
  }

  sync() {
    this.button = this.querySelector('[data-sticky-submit]');
    if (!this.button) return;
    const buttons = this.purchaseButtons();
    const target = buttons.length === 1 ? buttons[0] : null;
    this.button.disabled = !!target && this.blocked(target);
    this.button.setAttribute('aria-busy', String(!!target && (target.getAttribute('aria-busy') === 'true' || target.classList.contains('submitting') || target.classList.contains('loading'))));
    if (target) this.button.querySelector('span').textContent = (target.querySelector('span') || target).textContent.trim();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.mutations?.disconnect();
  }
}
if (!customElements.get('product-buy-bar')) customElements.define('product-buy-bar', ProductBuyBar);

class UpsellCard extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.image = this.querySelector('.card-media img');

    if (this.select) {
      this.select.addEventListener('change', (event) => {
        const selectedOption = event.target.options[event.target.selectedIndex];
        const newImgSrc = selectedOption.getAttribute('data-variant-img-src');
        if (newImgSrc) { this.updateImage(newImgSrc) }
      });
    }
  }

  updateImage(newImgSrc) {
    if (this.image && newImgSrc) {
      // Sanitize and validate URL to prevent XSS
      // Remove any whitespace and HTML entities
      const sanitized = String(newImgSrc).trim().replace(/[\s<>"']/g, '');

      // Reject dangerous protocols (check anywhere in string, not just start)
      const dangerousProtocols = /^(javascript|data|vbscript|file|about):/i;
      if (dangerousProtocols.test(sanitized)) {
        return; // Reject dangerous URLs
      }

      try {
        // Try to parse as absolute URL
        const url = new URL(sanitized, window.location.origin);
        // Only allow http and https protocols
        if (url.protocol === 'http:' || url.protocol === 'https:') {
          // Use the properly constructed URL to prevent any encoding issues
          this.image.src = url.href;
        }
      } catch (e) {
        // If URL parsing fails, it's likely a relative URL
        // Additional validation: ensure it doesn't contain dangerous patterns
        if (!sanitized.includes('javascript:') &&
            !sanitized.includes('data:') &&
            !sanitized.includes('vbscript:') &&
            !sanitized.includes('<') &&
            !sanitized.includes('>')) {
          // For relative URLs, ensure proper encoding
          this.image.src = sanitized;
        }
      }
    }
  }
}

customElements.define('upsell-card', UpsellCard);
